import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { format } from "date-fns";
import { toast } from "sonner";
import { Plus } from "lucide-react";

type Template = {
  id: string;
  action: string;
  display_name: string | null;
  system_prompt: string;
  user_prompt_template: string;
  default_model: string;
  fallback_model: string | null;
  default_temperature: number | null;
  default_max_tokens: number | null;
  expected_output_format: string;
  output_json_schema: any;
  use_prompt_cache: boolean;
  use_self_critique: boolean;
  use_multi_agent_debate: boolean;
  cost_per_1m_input_usd: number | null;
  cost_per_1m_output_usd: number | null;
  notes: string | null;
  is_active: boolean;
  updated_at: string;
};

const emptyTemplate: Partial<Template> = {
  action: "",
  display_name: "",
  system_prompt: "",
  user_prompt_template: "",
  default_model: "google/gemini-2.5-flash",
  fallback_model: "",
  default_temperature: 0.2,
  default_max_tokens: 2000,
  expected_output_format: "text",
  output_json_schema: null,
  use_prompt_cache: false,
  use_self_critique: false,
  use_multi_agent_debate: false,
  cost_per_1m_input_usd: 0,
  cost_per_1m_output_usd: 0,
  notes: "",
  is_active: true,
};

export default function ResearchPrompts() {
  const { data: canManage, isLoading: capLoading } = useResearchCapability("manage");
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<Template> | null>(null);
  const [jsonText, setJsonText] = useState<string>("");
  const [jsonError, setJsonError] = useState<string>("");

  const { data: templates, isLoading } = useQuery({
    queryKey: ["research_prompt_templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_ai_prompt_templates")
        .select("*")
        .order("action");
      if (error) throw error;
      return data as Template[];
    },
    enabled: !!canManage,
  });

  const save = useMutation({
    mutationFn: async (t: Partial<Template>) => {
      const payload: any = { ...t };
      if (payload.action) payload.action = payload.action.toLowerCase().replace(/\s+/g, "_");
      if (payload.expected_output_format !== "json") payload.output_json_schema = null;
      const { error } = payload.id
        ? await supabase.from("research_ai_prompt_templates").update(payload).eq("id", payload.id)
        : await supabase.from("research_ai_prompt_templates").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["research_prompt_templates"] });
      setEditing(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const openEdit = (t: Partial<Template>) => {
    setEditing(t);
    setJsonText(t.output_json_schema ? JSON.stringify(t.output_json_schema, null, 2) : "");
    setJsonError("");
  };

  const handleSave = () => {
    if (!editing) return;
    let schema = editing.output_json_schema;
    if (editing.expected_output_format === "json" && jsonText.trim()) {
      try {
        schema = JSON.parse(jsonText);
        setJsonError("");
      } catch (e: any) {
        setJsonError(e.message);
        return;
      }
    }
    save.mutate({ ...editing, output_json_schema: schema });
  };

  if (capLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!canManage) return <p className="text-sm text-muted-foreground">Requires manage capability.</p>;
  if (isLoading) return <p className="text-sm text-muted-foreground">Loading templates…</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Prompt templates</h2>
          <p className="text-sm text-muted-foreground">{templates?.length ?? 0} templates</p>
        </div>
        <Button onClick={() => openEdit(emptyTemplate)}>
          <Plus className="h-4 w-4 mr-1" /> New template
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Display name</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates?.map((t) => (
                <TableRow key={t.id} className="cursor-pointer" onClick={() => openEdit(t)}>
                  <TableCell className="font-mono text-xs">{t.action}</TableCell>
                  <TableCell>{t.display_name}</TableCell>
                  <TableCell className="font-mono text-xs">{t.default_model}</TableCell>
                  <TableCell>{t.expected_output_format}</TableCell>
                  <TableCell className="space-x-1">
                    {t.use_prompt_cache && <Badge variant="outline">cache</Badge>}
                    {t.use_self_critique && <Badge variant="outline">critique</Badge>}
                    {t.use_multi_agent_debate && <Badge variant="outline">debate</Badge>}
                  </TableCell>
                  <TableCell>{t.is_active ? <Badge>active</Badge> : <Badge variant="secondary">off</Badge>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {format(new Date(t.updated_at), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing?.id ? "Edit template" : "New template"}</SheetTitle>
          </SheetHeader>
          {editing && (
            <div className="space-y-3 mt-4">
              <Field label="Action (snake_case)">
                <Input value={editing.action ?? ""} onChange={(e) => setEditing({ ...editing, action: e.target.value })} />
              </Field>
              <Field label="Display name">
                <Input value={editing.display_name ?? ""} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} />
              </Field>
              <Field label="System prompt">
                <Textarea rows={5} value={editing.system_prompt ?? ""} onChange={(e) => setEditing({ ...editing, system_prompt: e.target.value })} />
              </Field>
              <Field label="User prompt template">
                <Textarea rows={5} value={editing.user_prompt_template ?? ""} onChange={(e) => setEditing({ ...editing, user_prompt_template: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Default model">
                  <Input value={editing.default_model ?? ""} onChange={(e) => setEditing({ ...editing, default_model: e.target.value })} />
                </Field>
                <Field label="Fallback model">
                  <Input value={editing.fallback_model ?? ""} onChange={(e) => setEditing({ ...editing, fallback_model: e.target.value })} />
                </Field>
                <Field label="Temperature">
                  <Input type="number" step="0.05" value={editing.default_temperature ?? 0} onChange={(e) => setEditing({ ...editing, default_temperature: Number(e.target.value) })} />
                </Field>
                <Field label="Max tokens">
                  <Input type="number" value={editing.default_max_tokens ?? 0} onChange={(e) => setEditing({ ...editing, default_max_tokens: Number(e.target.value) })} />
                </Field>
                <Field label="Output format">
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={editing.expected_output_format ?? "text"}
                    onChange={(e) => setEditing({ ...editing, expected_output_format: e.target.value })}
                  >
                    <option value="text">text</option>
                    <option value="json">json</option>
                    <option value="markdown">markdown</option>
                  </select>
                </Field>
                <Field label="Cost / 1M input (USD)">
                  <Input type="number" step="0.01" value={editing.cost_per_1m_input_usd ?? 0} onChange={(e) => setEditing({ ...editing, cost_per_1m_input_usd: Number(e.target.value) })} />
                </Field>
                <Field label="Cost / 1M output (USD)">
                  <Input type="number" step="0.01" value={editing.cost_per_1m_output_usd ?? 0} onChange={(e) => setEditing({ ...editing, cost_per_1m_output_usd: Number(e.target.value) })} />
                </Field>
              </div>
              {editing.expected_output_format === "json" && (
                <Field label="Output JSON schema">
                  <Textarea rows={8} className="font-mono text-xs" value={jsonText} onChange={(e) => setJsonText(e.target.value)} />
                  {jsonError && <p className="text-xs text-destructive mt-1">{jsonError}</p>}
                </Field>
              )}
              <div className="flex flex-wrap gap-4 pt-2">
                <Toggle label="Prompt cache" checked={!!editing.use_prompt_cache} onChange={(v) => setEditing({ ...editing, use_prompt_cache: v })} />
                <Toggle label="Self-critique" checked={!!editing.use_self_critique} onChange={(v) => setEditing({ ...editing, use_self_critique: v })} />
                <Toggle label="Multi-agent debate" checked={!!editing.use_multi_agent_debate} onChange={(v) => setEditing({ ...editing, use_multi_agent_debate: v })} />
                <Toggle label="Active" checked={!!editing.is_active} onChange={(v) => setEditing({ ...editing, is_active: v })} />
              </div>
              <Field label="Notes">
                <Textarea rows={2} value={editing.notes ?? ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </Field>
              <div className="flex gap-2 pt-3">
                <Button onClick={handleSave} disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Switch checked={checked} onCheckedChange={onChange} />
      {label}
    </label>
  );
}
